import type { ClientMeasurementId, MeasurementProfile } from "./types.js";

/**
 * Aldrich, Metric Pattern Cutting for Children's Wear and Babywear (4th ed.,
 * 2009), size charts on pp. 36–40 and 348, in cm. Read from
 * reference/books/aldrich-childrenswear-4e/size-charts.md (check a value
 * against the chart image there before pinning a test to it).
 *
 * Sizes are named by height. Each row lists one value per height, in the
 * order of `heights`; null where the book leaves the cell empty. Row names
 * follow the book (acrossBack is the full width, armLength is H–T), and
 * `childChartMeasurements` maps them to client measurement IDs.
 */
export type ChildSizeChartId = "babies" | "girls" | "boys" | "girlsPlus" | "boysPlus" | "girlsDeveloping";

export type ChildSizeChartRow =
  | "chest" | "bust" | "waist" | "seat" | "acrossBack" | "neck" | "shoulder" | "upperArm" | "wrist"
  | "scyeDepth" | "backNeckToWaist" | "waistToHip" | "cervicalHeight" | "waistToKnee" | "bodyRise"
  | "insideLeg" | "armLength" | "headCircumference" | "verticalTrunk" | "ankle" | "footLength" | "dart"
  // Garment measurements: spec constants defaulted from the chart, except
  // trouserBottomWidth, which is already a client measurement.
  | "cuffTwoPieceSleeve" | "cuffShirt" | "trouserBottomWidth" | "jeansBottomWidth";

export interface ChildSizeChart {
  page: number;
  heights: number[];
  rows: Partial<Record<ChildSizeChartRow, (number | null)[]>> & { age?: (string | null)[]; weight?: (string | null)[] };
}

export const childSizeCharts: Record<ChildSizeChartId, ChildSizeChart> = {
  babies: {
    page: 36,
    heights: [56, 64, 72, 80, 86, 92],
    rows: {
      weight: ["4–5", "6–7", "8", "9–10", "11–12", null],
      age: ["birth", "3m", "6m", "12m", "18m", "2yrs"],
      chest: [41, 44, 47, 50, 52, 54],
      waist: [41, 43, 45, 47, 49, 51],
      seat: [41, 44, 47, 50, 52, 54],
      acrossBack: [16.8, 18, 19.2, 20.4, 21.2, 22],
      neck: [22, 23, 24, 25, 25.5, 26],
      shoulder: [4.4, 5, 5.6, 6.2, 6.6, 7],
      upperArm: [14.4, 15.2, 16, 16.8, 17.4, 18],
      wrist: [9.8, 10.4, 11, 11.6, 12, 12.4],
      scyeDepth: [9, 9.8, 10.6, 11.4, 12, 12.6],
      backNeckToWaist: [15.8, 17.4, 19, 20.6, 21.8, 23],
      waistToHip: [7, 8, 9, 10, 10.75, 11.5],
      cervicalHeight: [42.2, 49.4, 56.6, 63.8, 69.2, 74.6],
      waistToKnee: [20.2, 22.8, 25.4, 28, 30, 32],
      bodyRise: [10.2, 11.5, 12.8, 14.1, 14.9, 15.7],
      insideLeg: [16, 21, 26, 31, 34.5, 38],
      armLength: [19.2, 22, 24.8, 27.6, 29.8, 32],
      headCircumference: [42.5, 44.5, 46.5, 48.5, 49.5, 50.5],
      verticalTrunk: [66, 73, 80, 87, 92, 97],
      ankle: [11, 12, 13, 14, 14.5, 15],
      footLength: [8.4, 9.6, 10.8, 12, 13, 14],
      cuffTwoPieceSleeve: [null, null, null, 9.4, 9.7, 10],
      cuffShirt: [null, null, null, 14.5, 14.8, 15.1],
      trouserBottomWidth: [null, null, null, 14.5, 15, 15.5],
      // The 80 cm value prints as 2.5; read as 12.5.
      jeansBottomWidth: [null, null, null, 12.5, 13, 13.5],
    },
  },
  girls: {
    page: 37,
    heights: [98, 104, 110, 116, 122, 128, 134, 140, 146, 152, 158, 164],
    rows: {
      age: ["3", "4", "5", "6", "7", "8", "9", "10", "11–14", null, null, null],
      chest: [55, 57, 59, 61, 63, 66, 69, 72, 75, 78, 81, 84],
      waist: [52, 54, 56, 58, 60, 61, 62, 63, 64, 65, 66, 67],
      seat: [56, 59, 62, 65, 68, 71, 74, 77, 80, 83, 86, 89],
      acrossBack: [22.8, 23.6, 24.4, 25.2, 26, 27.1, 28.2, 29.3, 30.4, 31.5, 32.6, 33.7],
      neck: [26.6, 27.2, 27.8, 28.4, 29, 30, 31, 32, 33, 34, 35, 36],
      shoulder: [7.4, 7.8, 8.2, 8.6, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5],
      upperArm: [18.5, 19, 19.5, 20, 20.5, 21.3, 22.1, 22.9, 23.7, 24.5, 25.3, 26.1],
      wrist: [12.8, 13, 13.2, 13.4, 13.6, 13.9, 14.2, 14.5, 14.8, 15.1, 15.4, 15.7],
      scyeDepth: [13.2, 13.8, 14.4, 15, 15.6, 16.3, 17, 17.7, 18.4, 19.1, 19.8, 20.5],
      backNeckToWaist: [24.2, 25.4, 26.6, 27.8, 29, 30.4, 31.8, 33.2, 34.6, 36, 37.4, 38.8],
      waistToHip: [12.3, 12.9, 13.5, 14.1, 14.7, 15.4, 16.1, 16.8, 17.5, 18.2, 18.9, 19.6],
      cervicalHeight: [80, 85.4, 90.8, 96.2, 101.6, 107, 112.4, 117.8, 123.2, 128.6, 134, 139.4],
      waistToKnee: [34, 36, 38, 40, 42, 44.2, 46.4, 48.6, 50.8, 53, 55.2, 57.4],
      bodyRise: [16.8, 17.6, 18.4, 19.2, 20, 21, 22, 23, 24, 25, 26, 27],
      insideLeg: [41, 44.5, 48, 51.5, 55, 58, 61, 64, 67, 70, 73, 76],
      armLength: [34, 36.5, 39, 41.5, 44, 46, 48, 50, 52, 54, 56, 58],
      headCircumference: [51.2, 51.8, 52.4, 53, 53.6, 54, 54.4, 54.8, 55.2, 55.6, 56, 56.4],
      ankle: [15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21],
      cuffTwoPieceSleeve: [10.2, 10.4, 10.6, 10.8, 11, 11.4, 11.8, 12.2, 12.6, 13, 13.4, 13.8],
      cuffShirt: [15.4, 15.8, 16.2, 16.6, 17, 17.5, 18, 18.5, 19.5, 20, 20.5, 21],
      trouserBottomWidth: [16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21, 21.5],
      jeansBottomWidth: [13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19],
    },
  },
  boys: {
    page: 38,
    heights: [98, 104, 110, 116, 122, 128, 134, 140, 146, 152, 158, 164, 170],
    rows: {
      age: ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13–14", null, null],
      chest: [55, 57, 59, 61, 64, 67, 70, 73, 76, 79, 82, 86, 90],
      waist: [52, 54, 56, 58, 60, 62, 64, 66, 68, 70, 72, 74, 76],
      seat: [55, 58, 61, 64, 67, 70, 73, 76, 79, 82, 85, 89, 93],
      acrossBack: [23.2, 24, 24.8, 25.6, 26.8, 28, 29.2, 30.4, 31.6, 32.8, 34, 35.6, 37.2],
      neck: [26.7, 27.3, 27.9, 28.5, 29.5, 30.5, 31.5, 32.5, 33.5, 34.5, 35.5, 36.5, 37.5],
      shoulder: [7.8, 8.2, 8.6, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13.1, 13.7],
      upperArm: [18.5, 19, 19.5, 20, 20.8, 21.6, 22.4, 23.2, 24, 24.8, 25.6, 26.6, 27.6],
      wrist: [13, 13.2, 13.4, 13.6, 14, 14.4, 14.8, 15.2, 15.6, 16, 16.5, 17, 17.5],
      scyeDepth: [13.2, 13.8, 14.4, 15, 15.8, 16.6, 17.4, 18.2, 19, 19.8, 20.6, 21.6, 22.6],
      backNeckToWaist: [24.2, 25.4, 26.6, 27.8, 29.2, 30.6, 32, 33.4, 34.8, 36.2, 37.6, 39.4, 41.2],
      waistToHip: [12, 12.6, 13.2, 13.8, 14.4, 15, 15.6, 16.2, 16.8, 17.4, 18, 18.8, 19.6],
      cervicalHeight: [80.4, 85.8, 91.2, 96.6, 102, 107.4, 112.8, 118.2, 123.6, 129, 134.4, 139.8, 145.2],
      bodyRise: [17.2, 18, 18.8, 19.6, 20.4, 21.2, 22, 22.8, 23.6, 24.4, 25.2, 26.2, 27.2],
      insideLeg: [41, 44.5, 48, 51.5, 55, 58, 61, 64, 67, 70, 73, 75.5, 78],
      armLength: [34.5, 37, 39.5, 42, 44.5, 47, 49.5, 52, 54.5, 57, 59, 61, 63],
      headCircumference: [52, 52.5, 53, 53.5, 54, 54.5, 55, 55.5, 56, 56.5, 57, 57.4, 57.8],
      cuffTwoPieceSleeve: [10.4, 10.6, 10.8, 11, 11.2, 11.6, 12, 12.4, 12.8, 13.2, 13.6, 14, 14.4],
      cuffShirt: [15.4, 15.8, 16.2, 16.6, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21],
      trouserBottomWidth: [16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21, 21.5, 22],
      jeansBottomWidth: [13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5],
    },
  },
  girlsPlus: {
    page: 39,
    heights: [98, 104, 110, 116, 122, 128, 134, 140, 146, 152, 158, 164],
    rows: {
      age: ["3", "4", "5", "6", "7", "8", "9", "10", "11–14", null, null, null],
      chest: [60, 62, 64, 66, 69, 72, 75, 78, 81, 84, 87, 90],
      waist: [56, 58, 60, 62, 64, 65, 66, 67, 68, 69, 70, 71],
      seat: [62, 65, 68, 71, 74, 77, 80, 83, 86, 89, 92, 95],
      acrossBack: [24.8, 25.6, 26.4, 27.2, 28.2, 29.3, 30.4, 31.5, 32.6, 33.7, 34.8, 35.9],
      neck: [28.2, 28.8, 29.4, 30, 31, 32, 33, 34, 35, 36, 37, 38],
      shoulder: [7.8, 8.2, 8.6, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13],
      upperArm: [19.5, 20, 20.5, 21, 21.8, 22.6, 23.4, 24.2, 25, 25.8, 26.6, 27.4],
      wrist: [13.2, 13.4, 13.6, 13.8, 14.2, 14.5, 14.8, 15.1, 15.4, 15.7, 16, 16.3],
      scyeDepth: [13.6, 14.2, 14.8, 15.2, 16, 16.7, 17.4, 18.1, 18.8, 19.5, 20.2, 20.9],
      backNeckToWaist: [24.2, 25.4, 26.6, 27.8, 29, 30.4, 31.8, 33.2, 34.6, 36, 37.4, 38.8],
      waistToHip: [12.3, 12.9, 13.5, 14.1, 14.8, 15.5, 16.2, 16.9, 17.6, 18.3, 19, 19.7],
      cervicalHeight: [80, 85.4, 90.8, 96.2, 101.6, 107, 112.4, 117.8, 123.2, 128.6, 134, 139.4],
      waistToKnee: [34, 36, 38, 40, 42, 44.2, 46.4, 48.6, 50.8, 53, 55.2, 57.4],
      bodyRise: [17.1, 17.9, 18.7, 19.5, 20.5, 21.5, 22.5, 23.5, 24.5, 25.5, 26.5, 27.5],
      insideLeg: [41, 44.5, 48, 51.5, 55, 58, 61, 64, 67, 70, 73, 76],
      armLength: [34, 36.5, 39, 41.5, 44, 46, 48, 50, 52, 54, 56, 58],
      headCircumference: [51.2, 51.8, 52.4, 53, 53.6, 54, 54.4, 54.8, 55.2, 55.6, 56, 56.4],
      ankle: [16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21, 21.5],
      cuffTwoPieceSleeve: [10.6, 10.8, 11, 11.2, 11.6, 12, 12.4, 12.8, 13.2, 13.6, 14, 14.4],
      cuffShirt: [16.2, 16.6, 17, 17.4, 18, 18.5, 19.5, 20, 20.5, 21, 21.5, 22],
      trouserBottomWidth: [17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21, 21.5, 22, 22.5],
      jeansBottomWidth: [15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5],
    },
  },
  boysPlus: {
    page: 40,
    heights: [98, 104, 110, 116, 122, 128, 134, 140, 146, 152, 158, 164, 170],
    rows: {
      age: ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13–14", null, null],
      chest: [60, 62, 64, 66, 70, 73, 76, 79, 82, 85, 90, 94, 98],
      waist: [58, 60, 62, 64, 66, 68, 70, 72, 74, 76, 80, 84, 88],
      seat: [61, 64, 67, 70, 73, 76, 79, 82, 85, 88, 93, 97, 101],
      acrossBack: [24.8, 25.6, 26.4, 27.2, 29.2, 30.4, 31.6, 32.8, 33, 34.2, 37, 38.6, 40.2],
      neck: [28.2, 28.8, 29.4, 30, 31, 32, 33, 34, 35, 36, 37.5, 38.5, 39.5],
      shoulder: [8.2, 8.6, 9, 9.4, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.6, 14.2],
      upperArm: [19.5, 20, 20.5, 21, 22, 22.8, 23.6, 24.4, 25.2, 26, 27, 28, 29],
      wrist: [13.6, 13.8, 14, 14.2, 14.6, 15, 15.4, 16, 16.4, 16.8, 17.5, 18, 18.5],
      scyeDepth: [13.8, 14.4, 15, 15.6, 16.2, 17, 17.8, 18.6, 19.4, 20.2, 21, 22, 23],
      backNeckToWaist: [24.6, 25.8, 27, 28.2, 29.6, 31, 32.4, 33.8, 35.2, 36.6, 38, 39.8, 41.6],
      waistToHip: [12, 12.6, 13.2, 13.8, 14.4, 15, 15.6, 16.2, 16.8, 17.4, 18, 18.8, 19.6],
      cervicalHeight: [80.4, 85.8, 91.2, 96.6, 102, 107.4, 112.8, 118.2, 123.6, 129, 134.4, 139.8, 145.2],
      bodyRise: [17.6, 18.4, 19.2, 20, 20.8, 21.6, 22.4, 23.2, 24, 24.8, 25.6, 26.6, 27.6],
      insideLeg: [41, 44.5, 48, 51.5, 55, 58, 61, 64, 67, 70, 73, 75.5, 78],
      armLength: [34.5, 37, 39.5, 42, 44.5, 47, 49.5, 52, 54.5, 57, 59, 61, 63],
      headCircumference: [52.5, 53, 53.5, 54, 54.5, 55, 55.5, 56, 56.5, 57, 57.4, 57.8, 58.2],
      cuffTwoPieceSleeve: [10.7, 10.9, 11.1, 11.3, 11.6, 12, 12.4, 12.8, 13.2, 13.6, 14, 14.5, 15],
      cuffShirt: [16.2, 16.6, 17, 17.4, 18, 18.5, 19, 19.5, 20, 20.5, 21, 21.5, 22],
      trouserBottomWidth: [17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5, 21, 21.5, 21.8, 22.1, 22.4],
      jeansBottomWidth: [15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 19.8, 20.1, 20.4],
    },
  },
  girlsDeveloping: {
    page: 348,
    heights: [146, 152, 158, 164],
    rows: {
      bust: [78, 81, 84, 87],
      waist: [63, 65, 67, 69],
      seat: [83, 86, 89, 92],
      acrossBack: [31, 32.2, 33.4, 34.6],
      neck: [33, 34, 35, 36],
      shoulder: [11, 11.4, 11.8, 12.2],
      dart: [2, 3, 4, 5],
      upperArm: [24, 24.8, 25.6, 26.4],
      wrist: [15, 15.3, 15.6, 15.9],
      scyeDepth: [18.3, 19, 19.7, 20.4],
      backNeckToWaist: [35.2, 36.6, 38, 39.4],
      waistToHip: [17.6, 18.4, 19.2, 20],
      cervicalHeight: [123.6, 129, 134.4, 139.8],
      waistToKnee: [50.8, 53, 55.2, 57.4],
      bodyRise: [24, 25, 26, 27],
      insideLeg: [68, 71, 74, 76],
      armLength: [54, 56, 58, 60],
      headCircumference: [55.2, 55.6, 56, 56.4],
      cuffTwoPieceSleeve: [12.2, 12.5, 12.8, 13.1],
      cuffShirt: [19.5, 20, 20.5, 21],
      trouserBottomWidth: [20, 20.5, 21, 21.5],
      jeansBottomWidth: [17.5, 18, 18.5, 19],
    },
  },
};

const chartInfo: Record<ChildSizeChartId, { label: string; sizeRange: "baby" | "child"; figure: "male" | "female"; ankleFrom?: ChildSizeChartId }> = {
  // The babies' chart is for boys and girls. The figure only picks the
  // mannequin; "male" keeps the women's rows off the fit page.
  babies: { label: "Baby", sizeRange: "baby", figure: "male" },
  girls: { label: "Girl", sizeRange: "child", figure: "female" },
  boys: { label: "Boy", sizeRange: "child", figure: "male", ankleFrom: "girls" },
  girlsPlus: { label: "Girl, plus", sizeRange: "child", figure: "female" },
  boysPlus: { label: "Boy, plus", sizeRange: "child", figure: "male", ankleFrom: "girlsPlus" },
  girlsDeveloping: { label: "Girl, developing", sizeRange: "child", figure: "female", ankleFrom: "girls" },
};

/** Chart rows that are body measurements, by the client measurement they fill. */
const clientRows: Partial<Record<ChildSizeChartRow, ClientMeasurementId>> = {
  chest: "chest",
  bust: "bust",
  waist: "naturalWaist",
  seat: "seat",
  acrossBack: "halfBack",
  neck: "neck",
  shoulder: "shoulder",
  upperArm: "armCircumference",
  wrist: "wrist",
  scyeDepth: "scyeDepth",
  backNeckToWaist: "backNeckToWaist",
  waistToHip: "waistToHip",
  cervicalHeight: "cervicalHeight",
  waistToKnee: "waistToKnee",
  bodyRise: "bodyRise",
  insideLeg: "insideLeg",
  // H–T, shoulder edge to wrist bone (p. 33): the one-piece sleeve length.
  armLength: "onePieceSleeveLength",
  headCircumference: "headCircumference",
  verticalTrunk: "verticalTrunk",
  ankle: "ankle",
  footLength: "footLength",
  dart: "bustDart",
  trouserBottomWidth: "trouserBottomWidth",
};

const hundredth = (value: number) => Math.round(value * 100) / 100;

/**
 * One chart row at a height, in cm. Between the chart's sizes the value is
 * read on a straight line between its neighbours, so any height in the
 * chart's range can be chosen. Undefined outside the range or where the book
 * leaves the row empty.
 */
export function childChartValue(chartId: ChildSizeChartId, row: ChildSizeChartRow, heightCm: number): number | undefined {
  const { heights, rows } = childSizeCharts[chartId];
  const values = rows[row];
  if (!values || heightCm < heights[0] || heightCm > heights[heights.length - 1]) return undefined;
  const upper = heights.findIndex((height) => height >= heightCm);
  const high = values[upper];
  if (heights[upper] === heightCm) return high ?? undefined;
  const low = values[upper - 1];
  if (low === null || high === null) return undefined;
  const t = (heightCm - heights[upper - 1]) / (heights[upper] - heights[upper - 1]);
  return hundredth(low + (high - low) * t);
}

/** The body measurements a chart gives for a height, in cm, under client measurement IDs. */
export function childChartMeasurements(chartId: ChildSizeChartId, heightCm: number): Partial<Record<ClientMeasurementId, number>> {
  const values: Partial<Record<ClientMeasurementId, number>> = { height: heightCm };
  for (const [row, id] of Object.entries(clientRows) as [ChildSizeChartRow, ClientMeasurementId][]) {
    const value = childChartValue(chartId, row, heightCm);
    if (value === undefined) continue;
    // The book's across back is the full width.
    values[id] = row === "acrossBack" ? hundredth(value / 2) : value;
  }
  return values;
}

function ageLabel(chartId: ChildSizeChartId, heightCm: number): string | undefined {
  const { heights, rows } = childSizeCharts[chartId];
  const index = heights.indexOf(heightCm);
  // A spanning age ("11–14") is printed once, under its first height.
  const age = rows.age?.slice(0, index + 1).reverse().find((value) => value !== null);
  if (index < 0 || !age) return undefined;
  if (age === "birth") return "newborn";
  const months = age.match(/^(\d+)m$/);
  if (months) return `about ${months[1]} months`;
  return `about ${age.replace(/yrs$/, "")} years`;
}

/**
 * Head size for the mannequin, estimated from the head circumference: width
 * and depth in an adult's proportion (162:205), and a head height that grows
 * from the depth at birth to 1.2 times it at 170 cm, as the face lengthens.
 */
function childStature(heightCm: number, headCircumferenceCm: number) {
  const across = (headCircumferenceCm * 10) / Math.PI;
  const headWidth = Math.round(across * (162 / 367) * 2);
  const headDepth = Math.round(across * (205 / 367) * 2);
  const face = 1 + 0.2 * Math.min(1, Math.max(0, (heightCm - 56) / (170 - 56)));
  return { height: heightCm * 10, headHeight: Math.round(headDepth * face), headWidth, headDepth };
}

/**
 * A standard profile for a chart size, generated from the chart. Rows the
 * chart lacks (the boys' ankle girth) are estimated from the girls' chart at
 * the same height, where it reaches.
 */
export function childChartProfile(chartId: ChildSizeChartId, heightCm: number): MeasurementProfile {
  const info = chartInfo[chartId];
  const values = childChartMeasurements(chartId, heightCm);
  const estimates: Partial<Record<ClientMeasurementId, number>> = {};
  if (values.ankle === undefined && info.ankleFrom) {
    const ankle = childChartValue(info.ankleFrom, "ankle", Math.min(heightCm, 164));
    if (ankle !== undefined) estimates.ankle = ankle;
  }
  const age = ageLabel(chartId, heightCm);
  return {
    label: `${info.label} ${heightCm} cm`,
    description: `Aldrich children's size chart, p. ${childSizeCharts[chartId].page}${age ? `, ${age}` : ""}`,
    figure: info.figure,
    sizeRange: info.sizeRange,
    stature: values.headCircumference !== undefined ? childStature(heightCm, values.headCircumference) : undefined,
    values,
    ...(Object.keys(estimates).length > 0 ? { estimates } : {}),
  };
}
